/**
 * SCRUM-158 (P-8) — Zod schemas for the Strategy Grid HTTP boundary.
 *
 * Endpoints covered (spec §10.1):
 *   #28 GET  /organizations/:slug/properties/:propertyId/strategy-grid   GetStrategyGrid
 *   #29 POST /admin/pricing/projection/rebuild                           RebuildSmartCellProjection
 *
 * `SmartCellSchema` mirrors `SmartCell` from
 * `apps/api/src/modules/pricing/domain/grid/smart-cell.ts`.
 *
 * `RebuildSmartCellProjectionRequestSchema` mirrors `RebuildScope` from
 * `apps/api/src/modules/pricing/application/grid/rebuild-smart-cell-projection.use-case.ts`.
 * Three scope kinds are supported:
 *   - 'property-and-range': { propertyId, rangeFrom, rangeTo }
 *   - 'property-all-future': { propertyId, horizonMonths }
 *   - 'organization-all-active': { horizonMonths }
 *
 * Adversarial tests: packages/validators/src/__tests__/pricing/strategy-grid.spec.ts
 */
import { z } from 'zod';
import { isoLocalDateString, instantString, centsSchema } from './_shared.js';
// ---------------------------------------------------------------------------
// GetStrategyGridQuerySchema — endpoint #28 query parameters
// ---------------------------------------------------------------------------
/**
 * GET .../strategy-grid query parameters.
 * Semi-open range: rangeFrom < rangeTo; max span 92 days.
 *
 * 92-day cap: the strategy grid is a dense per-day table; responses beyond
 * 92 days would be impractically large and degrade UX (spec §6.3.3).
 */
export const GetStrategyGridQuerySchema = z
    .object({
    rangeFrom: isoLocalDateString,
    rangeTo: isoLocalDateString,
})
    .refine((q) => q.rangeFrom < q.rangeTo, {
    message: 'rangeFrom must be before rangeTo (semi-open range)',
    path: ['rangeTo'],
})
    .refine((q) => {
    // Compute day difference from YYYY-MM-DD strings (lexicographic comparison
    // is safe because LocalDateSchema already validated the dates).
    const from = new Date(q.rangeFrom);
    const to = new Date(q.rangeTo);
    const diffMs = to.getTime() - from.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return diffDays <= 92;
}, {
    message: 'range must not exceed 92 days',
    path: ['rangeTo'],
});
// ---------------------------------------------------------------------------
// SmartCellSchema — mirrors SmartCell domain type (smart-cell.ts)
// ---------------------------------------------------------------------------
/**
 * SmartCellSchema — wire shape for one (propertyId, stayDate) cell.
 *
 * Field-by-field mapping from SmartCell domain type:
 *   - organizationId:              uuid string
 *   - propertyId:                  uuid string
 *   - stayDate:                    YYYY-MM-DD
 *   - currentNightlyRateCents:     Cents (renamed from currentNightlyRate)
 *   - baseRateCents:               Cents (renamed from baseRate)
 *   - floorRateCents:              Cents (renamed from floorRate)
 *   - soldPriceCents:              Cents | null (renamed from soldPrice)
 *   - isBlocked:                   boolean
 *   - externalPricingProvider:     'wheelhouse' | 'pricelabs' | null
 *   - externalPricingSyncEnabled:  boolean
 *   - rateSource:                  'desata' | 'wheelhouse' | 'pricelabs'
 *   - manualAdjustmentAllowed:     boolean
 *   - minStay:                     integer >= 1
 *   - cta:                         boolean
 *   - ctd:                         boolean
 *   - appliedPriceAdjustmentId:    uuid | null
 *   - appliedFloorRateOverrideId:  uuid | null
 *   - seasonGroupId:               uuid | null
 *   - sectionId:                   uuid | null
 *   - bucket:                      'weekday' | 'weekend'
 *   - recomputedAt:                ISO-8601 Instant with Z
 *   - syncedAt:                    ISO-8601 Instant with Z | null (SCRUM-248 WH-6;
 *                                  null = never provider-synced)
 */
export const SmartCellSchema = z.object({
    organizationId: z.string().uuid(),
    propertyId: z.string().uuid(),
    stayDate: isoLocalDateString,
    currentNightlyRateCents: centsSchema,
    baseRateCents: centsSchema,
    floorRateCents: centsSchema,
    soldPriceCents: centsSchema.nullable(),
    isBlocked: z.boolean(),
    externalPricingProvider: z.enum(['wheelhouse', 'pricelabs']).nullable(),
    externalPricingSyncEnabled: z.boolean(),
    rateSource: z.enum(['desata', 'wheelhouse', 'pricelabs']),
    manualAdjustmentAllowed: z.boolean(),
    minStay: z.number().int().min(1),
    cta: z.boolean(),
    ctd: z.boolean(),
    appliedPriceAdjustmentId: z.string().uuid().nullable(),
    appliedFloorRateOverrideId: z.string().uuid().nullable(),
    seasonGroupId: z.string().uuid().nullable(),
    sectionId: z.string().uuid().nullable(),
    bucket: z.enum(['weekday', 'weekend']),
    recomputedAt: instantString,
    syncedAt: instantString.nullable(),
});
// ---------------------------------------------------------------------------
// GetStrategyGridResponseSchema — endpoint #28 response
// ---------------------------------------------------------------------------
/**
 * GetStrategyGridResponseSchema — response for GET .../strategy-grid (endpoint #28).
 */
export const GetStrategyGridResponseSchema = z.object({
    cells: z.array(SmartCellSchema),
    rangeFrom: isoLocalDateString,
    rangeTo: isoLocalDateString,
    recomputedAt: instantString,
});
// ---------------------------------------------------------------------------
// RebuildSmartCellProjectionRequestSchema — endpoint #29
// Discriminated union mirrors RebuildScope from the use-case.
// ---------------------------------------------------------------------------
/**
 * RebuildSmartCellProjectionRequestSchema — POST /admin/pricing/projection/rebuild body.
 *
 * Three scope kinds (mirrors RebuildScope from rebuild-smart-cell-projection.use-case.ts):
 *   - 'property-and-range': explicit property + date range rebuild.
 *   - 'property-all-future': all future dates for one property (up to horizonMonths).
 *   - 'organization-all-active': all active properties in the organization.
 */
export const RebuildSmartCellProjectionRequestSchema = z.discriminatedUnion('scope', [
    z.object({
        scope: z.literal('property-and-range'),
        propertyId: z.string().uuid(),
        rangeFrom: isoLocalDateString,
        rangeTo: isoLocalDateString,
    }),
    z.object({
        scope: z.literal('property-all-future'),
        propertyId: z.string().uuid(),
        horizonMonths: z.number().int().min(1).max(60),
    }),
    z.object({
        scope: z.literal('organization-all-active'),
        horizonMonths: z.number().int().min(1).max(60),
    }),
]);
// ---------------------------------------------------------------------------
// RebuildSmartCellProjectionResponseSchema — endpoint #29 response
// ---------------------------------------------------------------------------
/**
 * RebuildSmartCellProjectionResponseSchema — POST .../rebuild response (endpoint #29).
 */
export const RebuildSmartCellProjectionResponseSchema = z.object({
    rebuildRunId: z.string().uuid(),
    affectedPropertyCount: z.number().int().nonnegative(),
    recomputedCellCount: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative(),
});
//# sourceMappingURL=strategy-grid.js.map